import { Trans } from "@lingui/macro"
import { useEffect, useState } from "react"
import { My } from "../../services/My"
import NotificationModal from "../main/NotificationModal"
import Page from "../../const/Page"
import { MemoModalEmitter } from "../rooms/right-menu/PanelMemos"
import hotkeys from "hotkeys-js"
import { useHistory } from "../../hooks/useRouterCompat";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu"
import { UserAvatar } from "./UserAvatar"
import { Util } from "../../services/Utils/Util"
import { MyProfileModal } from "./MyProfileModal"
import { TermsModal } from "./TermsModal"
import { AccountBlockingModal } from "./AccountBlockingModal"
import { FEATURE_USER_BLOCKING } from "../../config/features"
import { OPEN_PROFILE_EVENT } from "./ThemeManager"
import { WhatsNewHistoryModal } from "./WhatsNewModal"
import package_json from "../../../package.json"
import { Capacitor } from "@capacitor/core"


let deferredPrompt: any;
const handler = (e: any) => {
  e.preventDefault();
  deferredPrompt = e;
};

window.addEventListener('beforeinstallprompt', handler);

export const ProfilePart: React.FC = () => {
  const [isPWAEnabled, setIsPWAEnabled] = useState(false);
  const [isNative, setIsNative] = useState(false);
  const [canInstallPWA, setCanInstallPWA] = useState<boolean>(Boolean(deferredPrompt));

  // Modal states
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isBlockingModalOpen, setIsBlockingModalOpen] = useState(false);
  const [isWhatsNewModalOpen, setIsWhatsNewModalOpen] = useState(false);



  useEffect(() => {
    let mounted = true;
    import('../../pwa-push').then(mod => {
      if (mounted) {
        // 네이티브 앱 체크 먼저
        const native = mod.isNativeApp();
        setIsNative(native);
        // 네이티브가 아닐 때만 PWA 체크
        if (!native) {
          setIsPWAEnabled(mod.isPWA());
        }
      }
    }).catch(() => {
      setIsPWAEnabled(false);
      setIsNative(false);
    });
    return () => { mounted = false; };
  }, []);

  const [open, setOpen] = useState(false);
  const history = useHistory();

  // 설치 가능 상태를 beforeinstallprompt 이벤트로 감지 (Chrome 계열에서만 발생)
  useEffect(() => {
    const onBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      deferredPrompt = e;
      setCanInstallPWA(true);
    };
    const onAppInstalled = () => {
      // 설치 완료 시 버튼 숨김 처리
      setCanInstallPWA(false);
      deferredPrompt = null;
      setIsPWAEnabled(true);
    };
    // 초기값 동기화 (혹시 모듈 스코프 리스너가 먼저 잡았을 경우)
    if (deferredPrompt) setCanInstallPWA(true);
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  useEffect(() => {
    hotkeys('shift+s', (e, handler) => {
      e.preventDefault();
      history.push(Page.my_soribots);
    })
    return () => { hotkeys.unbind('shift+s') };
  }, []);

  // ThemeManager 등 외부에서 프로필 모달 열기 이벤트 수신
  useEffect(() => {
    const handleOpenProfile = () => setIsProfileModalOpen(true);
    window.addEventListener(OPEN_PROFILE_EVENT, handleOpenProfile);
    return () => window.removeEventListener(OPEN_PROFILE_EVENT, handleOpenProfile);
  }, []);

  return (
    <div className='flex items-center w-full gap-2 rounded-control'>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {/* Figma: GNB_button → flex-1, rounded-control, gap-2 */}
          <div className="flex items-center gap-2 rounded-control hover:bg-accent cursor-pointer transition-colors select-none outline-none group min-w-0 flex-1" data-cy="profile">
            <UserAvatar
              src={My.user.photoUrl || Util.ANONYMOUS_PHOTO_URL}
              name={My.user.name}
              className="w-12 h-12 flex-shrink-0"
            />
            {/* Figma: gap-1 between name and email */}
            <div className="flex flex-col gap-1 min-w-0 text-left">
              <div className="text-heading-sm font-bold text-[color:rgb(var(--text-title))] leading-[20px] truncate">{My.user.name}</div>
              <div className="text-label font-bold text-muted-foreground leading-[16px] truncate">
                {My.user.email}
              </div>
            </div>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="" align="start" sideOffset={0}>
          <DropdownMenuItem onClick={() => { setOpen(false); setIsProfileModalOpen(true); }} data-cy="profile" className="cursor-pointer">
            <Trans>My profile</Trans>
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => { setOpen(false); setIsWhatsNewModalOpen(true); }} className="cursor-pointer">
            <div className="flex items-center justify-between w-full">
               <span><Trans>Release Notes</Trans></span>
               <span className="text-xs text-muted-foreground ml-4">
                  v{package_json.version}
                  {Capacitor.getPlatform() === 'ios' && ` (${(package_json as any).iosBuildNumber})`}
                  {Capacitor.getPlatform() === 'android' && ` (${(package_json as any).androidBuildNumber})`}
                  {/* 커밋 SHA는 운영 노출 방지 위해 여기 표시하지 않음 —
                      dev 검증용은 좌하단 EnvVersionBadge(운영 빌드에선 DCE) 가 담당 */}
               </span>
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => { setOpen(false); history.push(Page.feedback); }} className="cursor-pointer">
            <Trans>Feedback</Trans>
          </DropdownMenuItem>

          {!isNative && !isPWAEnabled && canInstallPWA && (<>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={askInstallPWA} className="desktop-only cursor-pointer">
              <Trans>Install NEXTPOD desktop</Trans>
            </DropdownMenuItem>
          </>)}

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => { setOpen(false); setIsTermsModalOpen(true); }} data-cy="terms" className="cursor-pointer">
            <Trans>Terms of service</Trans>
          </DropdownMenuItem>

          {FEATURE_USER_BLOCKING && (<>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { setOpen(false); setIsBlockingModalOpen(true); }} className="cursor-pointer">
              <Trans>Account blocking</Trans>
            </DropdownMenuItem>
          </>)}

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logoutButton} data-cy="logout" className="cursor-pointer text-destructive focus:text-destructive">
            <Trans>Logout</Trans>
          </DropdownMenuItem>

          {getAdminButton()}
        </DropdownMenuContent>
      </DropdownMenu>
      <NotificationModal />

      {/* Modals */}
      {isProfileModalOpen && <MyProfileModal open={isProfileModalOpen} onOpenChange={setIsProfileModalOpen} />}
      {isTermsModalOpen && <TermsModal open={isTermsModalOpen} onOpenChange={setIsTermsModalOpen} />}
      {FEATURE_USER_BLOCKING && isBlockingModalOpen && <AccountBlockingModal open={isBlockingModalOpen} onOpenChange={setIsBlockingModalOpen} />}
      <WhatsNewHistoryModal open={isWhatsNewModalOpen} onOpenChange={setIsWhatsNewModalOpen} />

    </div>
  )

  function gotoDocSite() {
    window.open('https://sori.gitbook.io/sorichat', '_blank');
  }

  function helpText(text: string) {
    return <small className="ml-auto text-xs text-muted-foreground">{text}</small>
  }

  function openTextAssistant() {
    MemoModalEmitter.emit('text-assistant', ' ');
    setOpen(false);
  }

  async function askInstallPWA() {
    // deferredPrompt is a global variable we've been using in the sample to capture the `beforeinstallevent`
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    // Find out whether the user confirmed the installation or not
    const { outcome } = await deferredPrompt.userChoice;
    // The deferredPrompt can only be used once.
    deferredPrompt = null;
    // Act on the user's choice
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt.');
    } else if (outcome === 'dismissed') {
      console.log('User dismissed the install prompt');
    }
  }

  async function logoutButton() {
    const root = document.getElementById('root') || document.body;
    root.style.transition = 'opacity 0.3s ease-in-out';
    root.style.opacity = '0';

    setTimeout(async () => {
      await My.logoutUser();
      history.push('/login');
    }, 300);
  }

  function getAdminButton() {
    if (My.user.email.includes('@sunbisoft.com'))
      return (<>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Admin Pages</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => history.push('/admin_flagged')} className="cursor-pointer">
          <span><Trans>Flagged content</Trans></span>
        </DropdownMenuItem>
      </>)
    else
      return null
  }



}
